/* eslint-disable no-shadow */
import { useMemberData } from 'pages/FirstAccessFlow/context/MemberDataContext';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SmlsInputText } from '@smiles/smiles-ui-kit-react';
import { isValidPhoneNumber } from '../../../../services/core/helpers';
import { useConstants } from '../../context/ConstantsContext';

export function PhoneInput() {
  const NATIONAL_LENGHT = 11;

  enum InputErrorType {
    ERROR_DDD,
    ERROR_INVALID_PHONE,
  }

  const { countriesList } = useConstants();
  const { handler, response, setFormEdit } = useMemberData();
  const [value, setValue] = useState<string>('+55');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { t } = useTranslation('redo-register-v2');

  const setInputErrorHandling = (type: InputErrorType) => {
    const errorMessages: Record<InputErrorType, string> = {
      [InputErrorType.ERROR_INVALID_PHONE]: 'errors.invalid_phone',
      [InputErrorType.ERROR_DDD]: 'errors.invalid_ddd',
    };

    setErrorMessage(errorMessages[type]);
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = event.target.value;

    if (!phoneNumber) {
      setTimeout(() => {
        setValue('+');
      }, 100);
    }

    setValue(phoneNumber);
    setFormEdit();
  };

  const handleValidate = useCallback(
    (inputText: string) => {
      const isInitialPhone = inputText === '+55';

      if (isInitialPhone) {
        return true;
      }

      const validPhoneNumber = isValidPhoneNumber(inputText);

      const phoneIsValid = validPhoneNumber.isValid;

      if (!phoneIsValid) {
        handler({
          cellPhone: {
            DDI: '',
            DDD: '',
            number: '',
            formatted: {
              display: '',
            },
          },
        });

        setInputErrorHandling(InputErrorType.ERROR_INVALID_PHONE);

        return phoneIsValid;
      }

      const isNationalNumber = inputText.includes('+55');

      if (isNationalNumber && validPhoneNumber.phoneNumber) {
        const cleanedPhoneNumber = validPhoneNumber.phoneNumber.replace('+55', '').replace(/\D/g, '');
        const phoneLenght = cleanedPhoneNumber.length;

        if (phoneLenght < NATIONAL_LENGHT) {
          setInputErrorHandling(InputErrorType.ERROR_DDD);

          return false;
        }
      }

      const hasDDD = validPhoneNumber.DDD && !!validPhoneNumber.DDD.isValid && !!validPhoneNumber.DDD.value;
      const hasDDI = !!validPhoneNumber.DDI?.value && !!validPhoneNumber.DDI?.formatInternational;

      let phoneNumberDDD = '';
      let phoneNumberDDI = '';
      let formattedPhone = '';

      if (hasDDD) {
        phoneNumberDDD = validPhoneNumber.DDD?.value as string;
        formattedPhone = validPhoneNumber.DDD?.formatInternational as string;
      }

      if (hasDDI) {
        phoneNumberDDI = validPhoneNumber.DDI?.value as string;
      }

      const hasInternationNumber = validPhoneNumber.DDI?.formatInternational as string;

      if (!formattedPhone) {
        formattedPhone = hasInternationNumber;
      }

      let finalPhoneNumber = '';

      if (!phoneNumberDDD) {
        const [, ...restNumber] = hasInternationNumber?.split(' ') as string[];
        finalPhoneNumber = restNumber.join('').replace(/\D/g, '');
      } else {
        const [, , ...restNumber] = hasInternationNumber?.split(' ') as string[];
        finalPhoneNumber = restNumber.join('').replace(/\D/g, '');
      }

      handler({
        cellPhone: {
          DDI: phoneNumberDDI,
          DDD: phoneNumberDDD,
          number: finalPhoneNumber,
          formatted: {
            display: formattedPhone,
          },
        },
      });

      setValue(formattedPhone);

      return phoneIsValid;
    },
    [value],
  );

  const handlePaste = (pasteEvent: React.ClipboardEvent<HTMLInputElement>) => {
    const phoneNumber = pasteEvent.clipboardData.getData('text');
    const clearedNumber = phoneNumber.replace(/\D/g, '');

    setValue(`+${clearedNumber}`);
  };

  return (
    <div data-testid="first-access-phone-container">
      {countriesList && response.ready && (
        <SmlsInputText
          type="text"
          data-testid="first-access-phone-input"
          value={value}
          onChange={handleOnChange}
          validate={handleValidate}
          textLabel={t('labels.phone')}
          errorMessage={t(errorMessage)}
          onPaste={handlePaste}
        />
      )}
    </div>
  );
}
